import { Component, Inject, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { NaytettavaKirjaus } from '../kirjanpito.component'

import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { Asiakas, AsiakkaanMaksutapa } from 'app/_jaettu-lemonator/model/asiakas'
import { KirjauksienLuontityyppi, Kirjaus } from 'app/_jaettu-lemonator/model/kirjanpito'
import { AsiakasJaettuService } from 'app/_jaettu-lemonator/service/asiakas-jaettu.service'

export interface KirjausKopioiDialogData {
  naytettavaKirjaus: NaytettavaKirjaus
  maksutavat: AsiakkaanMaksutapa[]
  asiakas: Asiakas
}

interface KopiointiLomake {
  kirjauksenPvm: Date | null
  maksutapa: string | null
}

@Component({
  templateUrl: './kirjaus.kopioi.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjausKopioiDialog implements OnInit {
  lomake: FieldTree<KopiointiLomake>
  kaytettavatMaksutavat: AsiakkaanMaksutapa[]
  ensimmainenPaiva: Date = new Date(2015, 1, 1)
  viimeinenPaiva: Date = new Date()

  private _paivamaara: number = null
  private _maksutapa: string = null

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KirjausKopioiDialogData,
    private _dialogRef: MatDialogRef<KirjausKopioiDialog, Kirjaus>,
    private _dateService: DateService,
    private _formValidationService: FormValidationService,
    private _copyService: KopioijaPalvelu,
    private _asiakasJaettuService: AsiakasJaettuService,
    private _firebase: FirebaseLemonator,
  ) {
    this.kaytettavatMaksutavat =
      this.data.maksutavat?.filter(mt => {
        return mt.kirjauksienLuontityyppi === KirjauksienLuontityyppi.KASIN_SYOTETTAVA_TILIOTE || mt.kirjauksienLuontityyppi === KirjauksienLuontityyppi.TOSITTEISTA_GENEROITAVA_TILIOTE
      }) || []

    const asDate = this._dateService.numberToDate(this.data.naytettavaKirjaus.uusiPaivamaara || this.data.naytettavaKirjaus.kirjaus.p)
    this.lomake = form(signal<KopiointiLomake>({ kirjauksenPvm: asDate, maksutapa: this.data.naytettavaKirjaus.kirjaus.maksutavanTunniste }), polku => {
      required(polku.kirjauksenPvm)
      required(polku.maksutapa)
    })
    arvonMuutokset(this.lomake.kirjauksenPvm).subscribe(value => {
      this._paivamaara = this._dateService.localDateToNumber(this._dateService.dateToLocalDate(value))
    })
    arvonMuutokset(this.lomake.maksutapa).subscribe(value => {
      this._maksutapa = value
    })

    const ensimmainenTilikausi = this._asiakasJaettuService.annaEnsimmainenTilikausi(data.asiakas)
    if (ensimmainenTilikausi) {
      this.ensimmainenPaiva = this._dateService.localDateToDate(ensimmainenTilikausi.alkaa)
    }

    const viimeinenTilikausi = this._asiakasJaettuService.annaViimeinenTilikausi(data.asiakas)
    if (viimeinenTilikausi) {
      this.viimeinenPaiva = this._dateService.localDateToDate(viimeinenTilikausi.loppuu)
    }
  }

  ngOnInit() {}

  kopioi() {
    if (this.lomake().invalid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    const copy: Kirjaus = this._copyService.cloneObjectDeep(this.data.naytettavaKirjaus.kirjaus)
    copy.avain = this._firebase.firestoreCreateId()

    for (const rivi of copy.rivit ?? []) {
      rivi.av = this._firebase.firestoreCreateId()
    }

    if (this._paivamaara && copy.p !== this._paivamaara) {
      copy.p = this._paivamaara
    }

    if (this._maksutapa && copy.maksutavanTunniste !== this._maksutapa) {
      copy.maksutavanTunniste = this._maksutapa
    }

    this._dialogRef.close(copy)
  }
}
